import type { Lang } from './languages.js';

/**
 * Tile alphabet shared by every language.
 *
 * Policy (see GAME_RULES.md § Tiles and diacritics):
 * - A–Z are common to every language.
 * - Ñ is a distinct tile, only meaningful in Spanish (ñ is a letter of the Spanish alphabet).
 * - Ä, Ö, Ü are distinct tiles, only meaningful in German (as in German Scrabble).
 * - ß is spelled with two S tiles; ligatures œ/æ use two tiles (OE/AE).
 * - Other diacritics (é, è, ê, à, ç, ã, õ, í, ú, ü outside German, ñ outside Spanish…) fold to
 *   their base letter: the display keeps the correct spelling, the tile consumption is the base
 *   letter.
 */
export const TILE_ALPHABET = [
  'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M',
  'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z',
  'Ñ', 'Ä', 'Ö', 'Ü',
] as const; // prettier-ignore
export type Tile = (typeof TILE_ALPHABET)[number];
export const TILE_COUNT = TILE_ALPHABET.length;

/** Special tiles and the only languages in which they can be used. */
export const SPECIAL_TILE_LANGS: Readonly<Record<string, readonly Lang[]>> = {
  Ñ: ['es'],
  Ä: ['de'],
  Ö: ['de'],
  Ü: ['de'],
};

export const VOWEL_TILES: ReadonlySet<string> = new Set(['A', 'E', 'I', 'O', 'U', 'Ä', 'Ö', 'Ü']);

const TILE_INDEX = new Map<string, number>(TILE_ALPHABET.map((t, i) => [t, i]));

export function tileIndex(tile: string): number {
  const i = TILE_INDEX.get(tile);
  if (i === undefined) throw new Error(`Unknown tile: ${tile}`);
  return i;
}

export function isTile(value: string): value is Tile {
  return TILE_INDEX.has(value);
}

const COMBINING_MARKS = /\p{M}/gu;

/**
 * Converts a correctly spelled word to its tile key for a language, or null when the word
 * contains characters that cannot be played (digits, apostrophes, hyphens, spaces, other
 * scripts...). The key is the canonical form: two spellings with the same key are the same
 * playable word (e.g. FR "élève" and "élevé" → ELEVE).
 */
export function toTileKey(display: string, lang: Lang): string | null {
  const lower = display.normalize('NFC').toLowerCase();
  let out = '';
  for (const ch of lower) {
    switch (ch) {
      case 'ß':
        out += 'SS';
        continue;
      case 'œ':
        out += 'OE';
        continue;
      case 'æ':
        out += 'AE';
        continue;
      case 'ñ':
        out += lang === 'es' ? 'Ñ' : 'N';
        continue;
      case 'ä':
        out += lang === 'de' ? 'Ä' : 'A';
        continue;
      case 'ö':
        out += lang === 'de' ? 'Ö' : 'O';
        continue;
      case 'ü':
        out += lang === 'de' ? 'Ü' : 'U';
        continue;
      default:
        break;
    }
    if (ch >= 'a' && ch <= 'z') {
      out += ch.toUpperCase();
      continue;
    }
    const base = ch.normalize('NFD').replace(COMBINING_MARKS, '');
    if (base.length === 1 && base >= 'a' && base <= 'z') {
      out += base.toUpperCase();
      continue;
    }
    return null;
  }
  return out.length > 0 ? out : null;
}

/** Splits a key into tiles (keys only contain single-code-unit tiles). */
export function keyTiles(key: string): string[] {
  return Array.from(key);
}

/** Checks that a string only contains tiles of the alphabet. */
export function isValidKey(key: string): boolean {
  if (key.length === 0) return false;
  for (const ch of key) if (!TILE_INDEX.has(ch)) return false;
  return true;
}

/** Tile counts of a rack or key. */
export function tileCounts(tiles: Iterable<string>): Uint8Array {
  const counts = new Uint8Array(TILE_COUNT);
  for (const t of tiles) {
    const i = TILE_INDEX.get(t);
    if (i === undefined) throw new Error(`Unknown tile: ${t}`);
    counts[i] = (counts[i] ?? 0) + 1;
  }
  return counts;
}

/** True when every tile of the key is available in the rack (each tile used at most once). */
export function canFormKey(key: string, rackCounts: Uint8Array): boolean {
  const used = new Uint8Array(TILE_COUNT);
  for (const ch of key) {
    const i = TILE_INDEX.get(ch);
    if (i === undefined) return false;
    const u = (used[i] ?? 0) + 1;
    if (u > (rackCounts[i] ?? 0)) return false;
    used[i] = u;
  }
  return true;
}

/** Signature of a key: its tiles sorted by alphabet order (anagram class). */
export function keySignature(key: string): string {
  const counts = tileCounts(key);
  return countsToSignature(counts);
}

export function countsToSignature(counts: Uint8Array): string {
  let s = '';
  for (let i = 0; i < TILE_COUNT; i++) {
    const c = counts[i] ?? 0;
    if (c > 0) s += TILE_ALPHABET[i]!.repeat(c);
  }
  return s;
}

/**
 * Enumerates the distinct sub-multisets of a rack with a size in [minLen, maxLen], as
 * signatures. A 9-tile rack yields at most 466 signatures of length ≥ 3.
 */
export function subSignatures(rack: readonly string[], minLen: number, maxLen: number): string[] {
  const counts = tileCounts(rack);
  const letters: number[] = [];
  const maxCounts: number[] = [];
  for (let i = 0; i < TILE_COUNT; i++) {
    const c = counts[i] ?? 0;
    if (c > 0) {
      letters.push(i);
      maxCounts.push(c);
    }
  }
  const out: string[] = [];
  const current = new Array<number>(letters.length).fill(0);
  const walk = (pos: number, size: number): void => {
    if (pos === letters.length) {
      if (size >= minLen && size <= maxLen) {
        let s = '';
        for (let j = 0; j < letters.length; j++) {
          const c = current[j]!;
          if (c > 0) s += TILE_ALPHABET[letters[j]!]!.repeat(c);
        }
        out.push(s);
      }
      return;
    }
    const max = maxCounts[pos]!;
    for (let c = 0; c <= max; c++) {
      if (size + c > maxLen) break;
      current[pos] = c;
      walk(pos + 1, size + c);
    }
    current[pos] = 0;
  };
  walk(0, 0);
  return out;
}

/**
 * Maps a keyboard character to a tile, used by clients for physical keyboards.
 * Exact special tiles win (ä → Ä); otherwise the character is folded (é → E).
 */
/** Characters typed as a sequence of tiles (same policy as toTileKey). */
const MULTI_TILE_CHARS: Readonly<Record<string, readonly string[]>> = {
  ß: ['S', 'S'],
  ẞ: ['S', 'S'],
  œ: ['O', 'E'],
  Œ: ['O', 'E'],
  æ: ['A', 'E'],
  Æ: ['A', 'E'],
};

/**
 * Tile sequences a typed character may stand for, most specific first: ß → [S, S],
 * œ → [O, E], ä → [Ä] then [A], é → [E]. Empty when the character is not playable.
 */
export function charToTileSequences(ch: string): string[][] {
  const multi = MULTI_TILE_CHARS[ch.normalize('NFC')];
  if (multi) return [[...multi]];
  return charToTileCandidates(ch).map((t) => [t]);
}

export function charToTileCandidates(ch: string): string[] {
  const upper = ch.normalize('NFC').toUpperCase();
  if (upper.length !== 1) return [];
  const out: string[] = [];
  if (TILE_INDEX.has(upper)) out.push(upper);
  const base = upper.normalize('NFD').replace(COMBINING_MARKS, '');
  if (base !== upper && base.length === 1 && TILE_INDEX.has(base)) out.push(base);
  return out;
}
